import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSectionNavigation } from '../../hooks/useSectionNavigation';
import { trackEvent } from '../../analytics/tracker';
import ThemeToggle from '../ui/ThemeToggle';
import logo from '../../assets/SwiftTech_Logo.svg';
import logoWhite from '../../assets/SwiftTech_Logo_White.svg';

const Header: React.FC = () => {
  const { t, language, setLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { scrollToSection } = useSectionNavigation();
  const menuRef = useRef<HTMLDivElement>(null);
  const firstMenuItemRef = useRef<HTMLAnchorElement>(null);
  const lastMenuItemRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Keyboard navigation for mobile menu
  useEffect(() => {
    if (isMenuOpen && firstMenuItemRef.current) {
      firstMenuItemRef.current.focus();
    }
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    document.body.style.overflow = !isMenuOpen ? 'hidden' : '';
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    setIsMenuOpen(false);
    document.body.style.overflow = '';
    scrollToSection(e, sectionId);
  };

  // Trap focus in mobile menu
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isMenuOpen) return;

    if (e.key === 'Escape') {
      setIsMenuOpen(false);
      document.body.style.overflow = '';
      return;
    }

    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (document.activeElement === firstMenuItemRef.current) {
          e.preventDefault();
          lastMenuItemRef.current?.focus();
        }
      } else {
        if (document.activeElement === lastMenuItemRef.current) {
          e.preventDefault();
          firstMenuItemRef.current?.focus();
        }
      }
    }
  };

  return (
    <>
      {/* Skip Link for Accessibility (Web Development Skill) */}
      <a href="#main-content" className="skip-link">
        {t('skip_to_content') || 'Skip to main content'}
      </a>
      
      <header 
        className={`sw-header ${isScrolled ? 'scrolled' : ''}`}
        role="banner"
      >
        <div className="sw-container nav-container">
          <Link 
            to="/" 
            className="sw-logo"
            onClick={(e) => handleNavClick(e, 'home')}
            aria-label="Swiftech - Go to homepage"
          >
            <img 
              src={isScrolled ? logo : logoWhite}
              alt="Swiftech"
              className="h-10 w-auto object-contain"
              loading="eager"
            />
            <span className="sr-only">SWIFTECH</span>
          </Link>

          <nav 
            ref={menuRef}
            id="main-navigation"
            className={`sw-nav ${isMenuOpen ? 'active' : ''}`}
            role="navigation"
            aria-label="Main navigation"
            onKeyDown={handleKeyDown}
          >
            <a 
              href="#home" 
              className="nav-link"
              onClick={(e) => handleNavClick(e, 'home')}
              ref={firstMenuItemRef}
              aria-current="page"
            >
              {t('nav_home')}
            </a>
            <a 
              href="#services" 
              className="nav-link"
              onClick={(e) => handleNavClick(e, 'services')}
            >
              {t('nav_services')}
            </a>
            <a 
              href="#websites" 
              className="nav-link"
              onClick={(e) => handleNavClick(e, 'websites')}
            >
              {t('nav_websites')}
            </a>
            <a 
              href="#portfolio" 
              className="nav-link"
              onClick={(e) => handleNavClick(e, 'portfolio')}
            >
              {t('nav_portfolio')}
            </a>
            <a 
              href="#faq" 
              className="nav-link"
              onClick={(e) => handleNavClick(e, 'faq')}
            >
              {t('nav_faq')}
            </a>
            <Link 
              to="/swiftpos" 
              className="nav-link"
              onClick={() => setIsMenuOpen(false)}
            >
              {t('nav_pos')}
            </Link>
            <a 
              href="#contact" 
              className="nav-link"
              onClick={(e) => handleNavClick(e, 'contact')}
              ref={lastMenuItemRef}
            >
              {t('nav_contact')}
            </a>
          </nav>

          <div className="header-actions flex items-center">
            <div 
              className="flex items-center rounded-full p-1 mr-3"
              role="group"
              aria-label="Language selection"
              style={{ 
                backgroundColor: 'rgba(var(--bg-rgb), 0.05)',
                border: '1px solid var(--border)',
                gap: 'var(--touch-gap)'
              }}
            >
              <button
                onClick={() => setLanguage('en')}
                className="px-3 py-1 rounded-full text-[10px] font-black transition-all"
                style={{ 
                  backgroundColor: language === 'en' ? 'var(--primary)' : 'transparent',
                  color: language === 'en' ? 'white' : 'var(--text-muted)',
                  minWidth: 'var(--touch-min)',
                  minHeight: 'var(--touch-min)',
                  textDecoration: language === 'en' ? 'underline' : 'none',
                  textUnderlineOffset: '3px'
                }}
                aria-label={language === 'en' ? 'Current language: English' : 'Switch to English'}
                aria-pressed={language === 'en'}
                title={language === 'en' ? 'Current language: English' : 'Switch to English'}
              >
                EN
              </button>
              <button
                onClick={() => setLanguage('ar')}
                className="px-3 py-1 rounded-full text-[10px] font-black transition-all"
                style={{ 
                  backgroundColor: language === 'ar' ? 'var(--primary)' : 'transparent',
                  color: language === 'ar' ? 'white' : 'var(--text-muted)',
                  minWidth: 'var(--touch-min)',
                  minHeight: 'var(--touch-min)',
                  textDecoration: language === 'ar' ? 'underline' : 'none',
                  textUnderlineOffset: '3px'
                }}
                aria-label={language === 'ar' ? 'Current language: Arabic' : 'Switch to Arabic'}
                aria-pressed={language === 'ar'}
                title={language === 'ar' ? 'Current language: Arabic' : 'Switch to Arabic'}
              >
                AR
              </button>
            </div>
            <ThemeToggle />
            <span className="text-[8px] font-black uppercase tracking-widest hidden sm:block mr-2" style={{ color: 'var(--text-muted)' }} aria-hidden="true">
              {language === 'en' ? 'EN' : 'AR'}
            </span>
            <a 
              href="#contact" 
              className="sw-btn sw-btn-primary hide-mobile"
              onClick={(e) => {
                trackEvent('cta_click', undefined, { label: 'get_started', location: 'header' });
                handleNavClick(e, 'contact');
              }}
            >
              {t('btn_get_started')}
            </a>
            <button 
              className={`menu-toggle ${isMenuOpen ? 'is-active' : ''}`}
              onClick={toggleMenu}
              aria-label={isMenuOpen ? 'Close main menu' : 'Open main menu'}
              title={isMenuOpen ? 'Close main menu' : 'Open main menu'}
              aria-expanded={isMenuOpen}
              aria-controls="main-navigation"
            >
              <span className="bar" aria-hidden="true"></span>
              <span className="bar" aria-hidden="true"></span>
              <span className="bar" aria-hidden="true"></span>
            </button>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
